/**
 * Wasp Backend Integration Configuration
 * Connects Chef Lex v3.2 UI to existing mealprep Wasp backend
 */

// Wasp backend endpoints
// Derive URLs at runtime to avoid hardcoding localhost in deployments.
// - Prefer explicit env overrides when provided (Vite exposes VITE_* to client)
// - Fall back to current origin for same-origin deployments (e.g., Railway)
// - As a last resort in non-browser contexts, leave blank to use relative paths
const RUNTIME_ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
const ENV_BASE_URL = (typeof window !== 'undefined' ? (window as any).WASP_BASE_URL : undefined) || (import.meta as any)?.env?.VITE_WASP_BASE_URL;
const ENV_API_URL = (typeof window !== 'undefined' ? (window as any).WASP_API_URL : undefined) || (import.meta as any)?.env?.VITE_WASP_API_URL;

export const WASP_CONFIG = {
  // Base URLs (empty string means use relative URLs)
  BASE_URL: ENV_BASE_URL || RUNTIME_ORIGIN || '',
  API_URL: ENV_API_URL || '',
  
  // API Endpoints
  ENDPOINTS: {
    // Auth
    LOGIN: '/auth/login',
    SIGNUP: '/auth/signup', 
    ME: '/api/auth/me',
    
    // Meal Plans
    MEAL_PLANS: '/operations/getCurrentMealPlan',
    GENERATE_MEAL_PLAN: '/operations/generateMealPlan',
    REGENERATE_MEAL_PLAN: '/operations/regenerateMealPlan',
    GET_USER_MEAL_PLANS: '/operations/getUserMealPlans',
    
    // User Profile
    USER_PROFILE: '/operations/getUserProfile',
    UPDATE_PROFILE: '/operations/updateUserProfile',
    
    // Usage & Subscription
    USAGE_STATUS: '/operations/getUsageStatus',
    SUBSCRIPTION_STATUS: '/operations/getSubscriptionStatus',
    
    // AI Operations (our existing OpenAI/Ollama integration)
    AI_RECOMMENDATIONS: '/operations/generateAIRecommendations',
    AI_CHAT: '/operations/chatWithAI',
    
    // Analytics (Facebook-level tracking)
    TRACK_EVENT: '/api/analytics/track',
    TRACK_SWIPE: '/api/analytics/swipe',
    TRACK_INTERACTION: '/api/analytics/interaction',
    
    // Recipe Operations  
    SEARCH_RECIPES: '/operations/searchRecipes',
    GET_RECIPE_DETAILS: '/operations/getRecipeDetails',
    SAVE_RECIPE: '/operations/saveRecipe',
  }
};

// User preferences interface (matching our Wasp schema)
export interface WaspUserPreferences {
  dietaryRestrictions: string[];
  cookingSkill: string;
  availableTime: number;
  householdSize: number;
  favoriteIngredients: string[];
  cuisinePreferences: string[];
  allergies?: string[];
  mealTypes?: string[];
  cookingConfidence?: string;
  prepTime?: string;
  mainGoal?: string;
}

// Analytics event types for Facebook-level tracking
export interface AnalyticsEvent {
  userId: string;
  sessionId: string;
  event: string;
  properties: Record<string, any>;
  timestamp: number;
  userAgent: string;
  viewport: { width: number; height: number };
  url: string;
  referrer?: string;
  deviceType: 'mobile' | 'tablet' | 'desktop';
}

// Swipe pattern for AI personalization
export interface SwipeEvent {
  userId: string;
  sessionId: string;
  recipeId: string;
  action: 'liked' | 'saved' | 'skipped' | 'maybe';
  recipe: {
    title: string;
    cuisine: string;
    difficulty: string;
    prepTime: number;
    calories: number;
    tags: string[];
  };
  swipeMetrics: {
    swipeSpeed: number;    // milliseconds to decide
    swipeDistance: number; // pixels swiped
    hesitation: boolean;   // if user paused before swiping
  };
  contextData: {
    timeOfDay: string;
    previousSwipes: number;
    sessionDuration: number;
    screenSize: { width: number; height: number };
  };
  timestamp: number;
}

// Interaction tracking for UX optimization
export interface InteractionEvent {
  userId: string;
  sessionId: string;
  action: string;
  element: string;
  componentStack: string[];
  metadata: {
    duration?: number;
    clickPosition?: { x: number; y: number };
    scrollPosition?: number;
    error?: string;
  };
  timestamp: number;
}

// API client class for Wasp integration
export class WaspApiClient {
  private baseUrl: string;
  private apiUrl: string;
  private authToken: string | null = null;
  
  constructor() {
    this.baseUrl = WASP_CONFIG.BASE_URL;
    this.apiUrl = WASP_CONFIG.API_URL;
    
    // Note: Removed custom auth initialization that interfered with Wasp OAuth
    // Let Wasp handle authentication through its built-in system
  }

  // Note: Removed initializeAuth method that was bypassing Wasp's OAuth system

  public setAuthToken(token: string) {
    this.authToken = token;
    localStorage.setItem('wasp-auth-token', token);
  }

  private getHeaders() {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }
    
    return headers;
  }

  // Generic API call method
  private async apiCall(endpoint: string, options: RequestInit = {}) {
    // If API_URL is empty, use relative path to current origin
    const url = this.apiUrl ? `${this.apiUrl}${endpoint}` : endpoint;
    const response = await fetch(url, {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // Public wrapper methods for common HTTP operations
  async get<T>(path: string): Promise<T> {
    const res = await fetch(path, { credentials: 'include' });
    if (!res.ok) throw new Error(`GET ${path} failed: ${res.status}`);
    return (await res.json()) as T;
  }

  async post<T>(path: string, body?: any): Promise<T> {
    const res = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: body ? JSON.stringify(body) : undefined
    });
    if (!res.ok) throw new Error(`POST ${path} failed: ${res.status}`);
    return (await res.json()) as T;
  }

  // Auth methods
  async getCurrentUser() {
    return this.apiCall(WASP_CONFIG.ENDPOINTS.ME);
  }

  // Meal plan methods
  async getCurrentMealPlan() {
    return this.apiCall(WASP_CONFIG.ENDPOINTS.MEAL_PLANS);
  }

  async generateMealPlan(preferences: WaspUserPreferences) {
    return this.apiCall(WASP_CONFIG.ENDPOINTS.GENERATE_MEAL_PLAN, {
      method: 'POST',
      body: JSON.stringify({ preferences }),
    });
  }

  async regenerateMealPlan(mealPlanId: string) {
    return this.apiCall(WASP_CONFIG.ENDPOINTS.REGENERATE_MEAL_PLAN, {
      method: 'POST',
      body: JSON.stringify({ mealPlanId }),
    });
  }

  // AI methods (using our existing OpenAI/Ollama integration)
  async getAIRecommendations(swipeHistory: SwipeEvent[], preferences: WaspUserPreferences) {
    return this.apiCall(WASP_CONFIG.ENDPOINTS.AI_RECOMMENDATIONS, {
      method: 'POST',
      body: JSON.stringify({ swipeHistory, preferences }),
    });
  }

  async chatWithChefLex(message: string, context: any) {
    return this.apiCall(WASP_CONFIG.ENDPOINTS.AI_CHAT, {
      method: 'POST',
      body: JSON.stringify({ message, context }),
    });
  }

  // Recipe methods
  async searchRecipes(query: string, filters: any = {}) {
    // POST JSON to match server operation schema
    return this.apiCall(WASP_CONFIG.ENDPOINTS.SEARCH_RECIPES, {
      method: 'POST',
      body: JSON.stringify({ query, ...filters }),
    });
  }

  async getRecipeDetails(recipeId: string) {
    return this.apiCall(`${WASP_CONFIG.ENDPOINTS.GET_RECIPE_DETAILS}?id=${recipeId}`);
  }

  async saveRecipe(recipeId: string) {
    return this.apiCall(WASP_CONFIG.ENDPOINTS.SAVE_RECIPE, {
      method: 'POST',
      body: JSON.stringify({ recipeId }),
    });
  }

  // Analytics methods (Facebook-level tracking)
  async trackEvent(event: AnalyticsEvent) {
    return this.apiCall(WASP_CONFIG.ENDPOINTS.TRACK_EVENT, {
      method: 'POST',
      body: JSON.stringify(event),
    });
  }

  async trackSwipe(swipeEvent: SwipeEvent) {
    return this.apiCall(WASP_CONFIG.ENDPOINTS.TRACK_SWIPE, {
      method: 'POST',
      body: JSON.stringify(swipeEvent),
    });
  }

  async trackInteraction(interaction: InteractionEvent) {
    return this.apiCall(WASP_CONFIG.ENDPOINTS.TRACK_INTERACTION, {
      method: 'POST',
      body: JSON.stringify(interaction),
    });
  }
}

// Singleton instance
export const waspApi = new WaspApiClient();

// Facebook-level analytics tracker
export class AnalyticsTracker {
  private sessionId: string;
  private userId: string | null = null;
  private sessionStartTime: number;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.sessionStartTime = Date.now();
    this.initializeTracking();
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2)}`;
  }

  private initializeTracking() {
    // Track page visibility
    document.addEventListener('visibilitychange', () => {
      this.trackEvent('page_visibility_change', {
        visible: !document.hidden,
        duration: Date.now() - this.sessionStartTime
      });
    });

    // Track mouse movements (sampled)
    let mouseMoveCount = 0;
    document.addEventListener('mousemove', () => {
      mouseMoveCount++;
      if (mouseMoveCount % 50 === 0) { // Sample every 50th move
        this.trackEvent('mouse_activity', {
          moves: mouseMoveCount,
          sessionDuration: Date.now() - this.sessionStartTime
        });
      }
    });

    // Track scrolling behavior
    let scrollTimeout: NodeJS.Timeout;
    document.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        this.trackEvent('scroll_behavior', {
          scrollY: window.scrollY,
          maxScroll: document.documentElement.scrollHeight - window.innerHeight,
          scrollPercent: (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
        });
      }, 250);
    });

    // Track window focus/blur
    window.addEventListener('focus', () => {
      this.trackEvent('window_focus', { 
        duration: Date.now() - this.sessionStartTime 
      });
    });

    window.addEventListener('blur', () => {
      this.trackEvent('window_blur', { 
        duration: Date.now() - this.sessionStartTime 
      });
    });
  }

  public setUserId(userId: string) {
    this.userId = userId;
  }

  public trackEvent(eventName: string, properties: Record<string, any> = {}) {
    if (!this.userId) return;

    const event: AnalyticsEvent = {
      userId: this.userId,
      sessionId: this.sessionId,
      event: eventName,
      properties: {
        ...properties,
        sessionDuration: Date.now() - this.sessionStartTime,
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        cookiesEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine
      },
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      url: window.location.href,
      referrer: document.referrer,
      deviceType: this.getDeviceType()
    };

    // Send to our Wasp backend
    waspApi.trackEvent(event).catch(error => {
      console.warn('Analytics tracking failed:', error);
    });
  }

  public trackSwipe(swipeEvent: Omit<SwipeEvent, 'userId' | 'sessionId' | 'timestamp'>) {
    if (!this.userId) return;

    const fullSwipeEvent: SwipeEvent = {
      ...swipeEvent,
      userId: this.userId,
      sessionId: this.sessionId,
      timestamp: Date.now()
    };

    waspApi.trackSwipe(fullSwipeEvent).catch(error => {
      console.warn('Swipe tracking failed:', error);
    });
  }

  public trackInteraction(interaction: Omit<InteractionEvent, 'userId' | 'sessionId' | 'timestamp'>) {
    if (!this.userId) return;

    const fullInteraction: InteractionEvent = {
      ...interaction,
      userId: this.userId,
      sessionId: this.sessionId,
      timestamp: Date.now()
    };

    waspApi.trackInteraction(fullInteraction).catch(error => {
      console.warn('Interaction tracking failed:', error);
    });
  }

  private getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
    const width = window.innerWidth;
    if (width <= 768) return 'mobile';
    if (width <= 1024) return 'tablet';
    return 'desktop';
  }
}

// Global analytics instance
export const analytics = new AnalyticsTracker();
